/**
 * @name useHistoricoData
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Hook database-driven para histórico de transações usando vw_extrato_movimentacoes_aluno
 * @package components/pages/gestao-financeira/historico
 */

import { useUserContext } from '@/contexts/UserContext';
import { useAlert } from '@/hooks/useAlert';
import { useLoading } from '@/hooks/useLoading';
import { vwExtratoMovimentacoesAlunoService } from '@/services/supabase/views/VwExtratoMovimentacoesAlunoService';
import { exportData } from '@/utils/secureExportUtils';
import { supabase } from '../../../../../supabaseConfig';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Types específicos para histórico de transações
interface MovimentacaoHistorico {
     id: number | null;
     pessoa: string | null;
     tipo_pessoa: string | null;
     tipo_movimentacao: string | null;
     tipo_origem: string | null;
     valor: number | null;
     saldo_anterior: number | null;
     saldo_posterior: number | null;
     descricao: string | null;
     data_movimentacao: string | null;
     criado_por: string | null;
     organizacao: string | null;
}

interface FiltrosHistorico {
     tipo: 'todos' | 'credito' | 'debito';
     periodo: 'ultimos_7_dias' | 'ultimos_30_dias' | 'ultimos_90_dias' | 'este_ano';
     dataInicio?: Date;
     dataFim?: Date;
}

/**
 * Hook database-driven para gestão de histórico de transações
 *
 * Funcionalidades:
 * - Consome vw_extrato_movimentacoes_aluno para histórico completo
 * - Filtros por tipo de movimentação e período
 * - Loading states e tratamento de erros
 * - Formatação de dados para exibição
 * - Export de dados
 * - Validação de organização e usuário
 */
export const useHistoricoData = () => {
     const { userData } = useUserContext();
     const { showSuccess, showError } = useAlert();
     const { withGlobalLoading, loadingStates, setPageLoading } = useLoading();

     // Helper para verificar estado de loading
     const isLoading = loadingStates['page-loading'] || false;

     // Referências estáveis para evitar dependências
     const showErrorRef = useRef(showError);
     const showSuccessRef = useRef(showSuccess);
     
     // Refs para controle de carregamento
     const isLoadingRef = useRef(false);
     const hasLoadedRef = useRef(false);
     
     // Atualizar refs sempre que as funções mudarem
     useEffect(() => {
          showErrorRef.current = showError;
          showSuccessRef.current = showSuccess;
     }, [showError, showSuccess]);

     // Estados locais
     const [movimentacoes, setMovimentacoes] = useState<MovimentacaoHistorico[]>([]);
     const [movimentacoesFiltradas, setMovimentacoesFiltradas] = useState<MovimentacaoHistorico[]>([]);
     const [filtros, setFiltros] = useState<FiltrosHistorico>({
          tipo: 'todos',
          periodo: 'ultimos_30_dias',
     });

     // Obter serviço de forma estável
     const movimentacoesService = useMemo(() => vwExtratoMovimentacoesAlunoService, []);

     // Validação de dados do usuário - função memorizada
     const isValidUser = useCallback(() => {
          if (!userData?.id) {
               console.warn('⚠️ ID do usuário não disponível');
               return false;
          }

          if (!userData?.fk_id_organizacao) {
               console.warn('⚠️ ID da organização não disponível');
               return false;
          }

          if (isNaN(userData.fk_id_organizacao) || userData.fk_id_organizacao <= 0) {
               console.warn('⚠️ ID da organização inválido:', userData.fk_id_organizacao);
               return false;
          }

          return true;
     }, [userData?.id, userData?.fk_id_organizacao]);

     // Atualizar filtros
     const updateFiltros = useCallback((novosFiltros: Partial<FiltrosHistorico>) => {
          setFiltros((prev) => ({ ...prev, ...novosFiltros }));
     }, []);

     // Função para carregar dados - extraída para reutilização
     const carregarDados = useCallback(async () => {
          if (!userData?.id || !userData?.fk_id_organizacao) {
               console.log('⚠️ Dados do usuário inválidos para carregar');
               return;
          }

          if (isNaN(userData.fk_id_organizacao) || userData.fk_id_organizacao <= 0) {
               console.warn('⚠️ ID da organização inválido:', userData.fk_id_organizacao);
               return;
          }

          const organizationId = userData.fk_id_organizacao;
          const responsavelId = userData.id;

          console.log('🔄 Carregando histórico de movimentações');
          console.log('   └── Responsável ID:', responsavelId);
          console.log('   └── Organização ID:', organizationId);

          try {
               // Carregar dependentes
               const { data: dependentesData, error: dependentesError } = await supabase
                    .from('pessoas_dependentes')
                    .select('fk_id_aluno, aluno:pessoas!fk_id_aluno(id, nome)')
                    .eq('fk_id_organizacao', organizationId)
                    .eq('fk_id_responsavel', responsavelId);

               if (dependentesError) {
                    showErrorRef.current('Erro ao carregar dependentes: ' + dependentesError.message);
                    setMovimentacoes([]);
                    return;
               }

               const dependentesValidos = (dependentesData || [])
                    .map((item: any) => {
                         const aluno = Array.isArray(item.aluno) ? item.aluno[0] : item.aluno;
                         if (!aluno?.id) return null;
                         return {
                              id: Number(aluno.id),
                              nome: aluno.nome || `Dependente ${aluno.id}`,
                         };
                    })
                    .filter((item: any) => Boolean(item));

               if (dependentesValidos.length === 0) {
                    console.log('ℹ️ Nenhum dependente encontrado');
                    setMovimentacoes([]);
                    return;
               }

               // Buscar movimentações para cada dependente
               const respostasMovimentacoes = await Promise.all(
                    dependentesValidos.map((dependente: any) => {
                         console.log(`🔄 Carregando movimentações para aluno: ${dependente.id} (${dependente.nome})`);
                         return movimentacoesService.getExtratoAluno(organizationId, dependente.id);
                    }),
               );

               const primeiraComErro = respostasMovimentacoes.find((response: any) => response.error);
               if (primeiraComErro?.error) {
                    console.error('❌ Erro na query:', primeiraComErro.error);
                    showErrorRef.current('Erro ao carregar histórico: ' + primeiraComErro.error.message);
                    setMovimentacoes([]);
                    return;
               }

               // Consolidar dados
               const dataFlat = respostasMovimentacoes.flatMap((response: any) => {
                    const data = response.data || [];
                    return Array.isArray(data) ? data : [data];
               });

               console.log('📋 Total de movimentações consolidadas:', dataFlat.length);

               // Mapear para o formato esperado
               const mapped = dataFlat
                    .map((mov: any) => ({
                         id: mov.id ?? null,
                         pessoa: mov.nome_aluno ?? null,
                         tipo_pessoa: null,
                         tipo_movimentacao: mov.tipo_movimentacao ?? null,
                         tipo_origem: mov.tipo_origem ?? null,
                         valor: mov.valor ?? null,
                         saldo_anterior: mov.saldo_anterior ?? null,
                         saldo_posterior: mov.saldo_posterior ?? null,
                         descricao: mov.descricao ?? null,
                         data_movimentacao: mov.data_movimentacao ?? null,
                         criado_por: mov.criado_por ?? null,
                         organizacao: null,
                    }))
                    .sort((a: MovimentacaoHistorico, b: MovimentacaoHistorico) => {
                         if (!a.data_movimentacao || !b.data_movimentacao) return 0;
                         return new Date(b.data_movimentacao).getTime() - new Date(a.data_movimentacao).getTime();
                    });

               setMovimentacoes(mapped);
               console.log('✅ Histórico carregado com sucesso:', mapped.length, 'registros');
          } catch (error) {
               console.error('❌ Erro ao carregar histórico:', error);
               showErrorRef.current('Erro ao carregar histórico: ' + (error as Error).message);
               setMovimentacoes([]);
          }
     }, [userData?.id, userData?.fk_id_organizacao, movimentacoesService]);

     // Função para recarregar dados manualmente
     const recarregar = useCallback(() => {
          console.log('🔄 Recarregando dados...');
          return carregarDados();
     }, [carregarDados]);

     // Função para exportar dados em Excel usando util
     const exportarDados = useCallback(async () => {
          return withGlobalLoading(async () => {
               try {
                    const dadosExport = movimentacoesFiltradas.map((mov) => ({
                         Data: mov.data_movimentacao ? new Date(mov.data_movimentacao).toLocaleDateString('pt-BR') : '',
                         Pessoa: mov.pessoa || '',
                         Tipo: mov.tipo_movimentacao || '',
                         Origem: mov.tipo_origem || '',
                         Valor: mov.valor || 0,
                         'Saldo Anterior': mov.saldo_anterior || 0,
                         'Saldo Posterior': mov.saldo_posterior || 0,
                         Descrição: mov.descricao || '',
                         'Criado Por': mov.criado_por || '',
                    }));

                    exportData(dadosExport, 'excel', {
                         filename: `historico-transacoes-${new Date().toISOString().split('T')[0]}`,
                         sheetName: 'Histórico',
                    });
                    showSuccessRef.current('Arquivo exportado com sucesso!');
               } catch (error) {
                    console.error('❌ Erro ao exportar dados:', error);
                    showErrorRef.current('Erro ao exportar dados: ' + (error as Error).message);
               }
          });
     }, [movimentacoesFiltradas, withGlobalLoading]);

     // Efeito para carregar dados iniciais - apenas quando userId ou organizacao mudar
     useEffect(() => {
          if (userData?.id && userData?.fk_id_organizacao && !hasLoadedRef.current && !isLoadingRef.current) {
               console.log('✅ [useHistoricoData] Iniciando carregamento inicial de dados...');
               hasLoadedRef.current = true;
               isLoadingRef.current = true;
               setPageLoading(true);
               
               carregarDados()
                    .finally(() => {
                         setPageLoading(false);
                         isLoadingRef.current = false;
                    });
          } else {
               console.log('⚠️ Usuário inválido ou dados já carregados, não carregando dados');
          }
     }, [userData?.id, userData?.fk_id_organizacao, carregarDados, setPageLoading]);

     // Efeito para aplicar filtros quando dados ou filtros mudarem
     useEffect(() => {
          let movimentacoesFiltradas = [...movimentacoes];

          // Filtrar por tipo
          if (filtros.tipo !== 'todos') {
               movimentacoesFiltradas = movimentacoesFiltradas.filter((mov) => mov.tipo_movimentacao === filtros.tipo);
          }

          // Filtrar por período
          const agora = new Date();
          let dataLimite: Date;

          switch (filtros.periodo) {
               case 'ultimos_7_dias':
                    dataLimite = new Date(agora.getTime() - 7 * 24 * 60 * 60 * 1000);
                    break;
               case 'ultimos_30_dias':
                    dataLimite = new Date(agora.getTime() - 30 * 24 * 60 * 60 * 1000);
                    break;
               case 'ultimos_90_dias':
                    dataLimite = new Date(agora.getTime() - 90 * 24 * 60 * 60 * 1000);
                    break;
               case 'este_ano':
                    dataLimite = new Date(agora.getFullYear(), 0, 1);
                    break;
               default:
                    dataLimite = new Date(agora.getTime() - 30 * 24 * 60 * 60 * 1000);
          }

          movimentacoesFiltradas = movimentacoesFiltradas.filter((mov) => {
               if (!mov.data_movimentacao) return false;
               const dataMovimentacao = new Date(mov.data_movimentacao);
               return dataMovimentacao >= dataLimite;
          });

          // Ordenar por data (mais recente primeiro)
          movimentacoesFiltradas.sort((a, b) => {
               if (!a.data_movimentacao || !b.data_movimentacao) return 0;
               return new Date(b.data_movimentacao).getTime() - new Date(a.data_movimentacao).getTime();
          });

          setMovimentacoesFiltradas(movimentacoesFiltradas);
     }, [movimentacoes, filtros.tipo, filtros.periodo]);

     // Métricas calculadas - memorizada para evitar re-renders
     const metricas = useMemo(() => ({
          totalMovimentacoes: movimentacoes.length,
          totalMovimentacoesFiltradas: movimentacoesFiltradas.length,
          totalCreditos: movimentacoesFiltradas.filter((mov) => mov.tipo_movimentacao === 'credito').reduce((total, mov) => total + (mov.valor || 0), 0),
          totalDebitos: movimentacoesFiltradas.filter((mov) => mov.tipo_movimentacao === 'debito').reduce((total, mov) => total + (mov.valor || 0), 0),
          ultimaMovimentacao: movimentacoesFiltradas.length > 0 ? movimentacoesFiltradas[0] : null,
     }), [movimentacoes.length, movimentacoesFiltradas]);

     // Valores de contexto memorizados
     const contextInfo = useMemo(() => ({
          organizationId: userData?.fk_id_organizacao || null,
          responsavelId: userData?.id || null,
          isValidUser: isValidUser(),
     }), [userData?.fk_id_organizacao, userData?.id, isValidUser]);

     return {
          // Estados
          movimentacoes: movimentacoesFiltradas,
          loading: isLoading, // ✅ Usando LoadingContext
          filtros,
          metricas,

          // Ações
          updateFiltros,
          recarregar,
          exportarDados,

          // Context info
          ...contextInfo,

          // Metadados - memorizado para evitar re-renders
          metadados: useMemo(() => ({
               titulo: 'Histórico de Transações',
               subtitulo: 'Acompanhe todas as movimentações financeiras da sua conta',
               paginaTitulo: 'Histórico',
               breadcrumbs: [
                    { label: 'Início', path: '/' },
                    { label: 'Gestão Financeira', path: '/gestao-financeira' },
                    { label: 'Histórico', path: '/gestao-financeira/historico' },
               ],
          }), []),
     };
};

export default useHistoricoData;
